/**
 * Centralized API response envelope parser.
 *
 * Normalizes responses from:
 * 1. New Phase 2 envelope: { success: true, data: T }
 * 2. Paginated envelope: { data: T, next_page?: string | null } or { success: true, data: T, next_page?: string | null }
 */
export function parseApiResponse<T>(responseJSON: unknown): T | undefined {
  if (!responseJSON || typeof responseJSON !== 'object') {
    return undefined;
  }

  const record = responseJSON as Record<string, unknown>;

  // Case 1: Standard success envelope { success: true, data: T }
  if (record.success === true && 'data' in record) {
    return record.data as T;
  }

  // Case 2: Paginated payload { data: T, next_page: ... } without success flag
  if ('data' in record) {
    return record.data as T;
  }

  return undefined;
}

export interface PaginatedResult<T> {
  data: T[];
  next_page: string | null;
}

/**
 * Extracts items array and next_page cursor from paginated responses.
 * Normalizes:
 * - { success: true, data: T[], next_page: string | null }
 * - { data: T[], next_page: string | null }
 */
export function parsePaginatedResponse<T>(responseJSON: unknown): PaginatedResult<T> {
  if (!responseJSON || typeof responseJSON !== 'object') {
    return { data: [], next_page: null };
  }

  const record = responseJSON as Record<string, unknown>;
  let items: T[] = [];

  if (record.success === true && 'data' in record && Array.isArray(record.data)) {
    items = record.data as T[];
  } else if ('data' in record && Array.isArray(record.data)) {
    items = record.data as T[];
  }

  const nextPage = typeof record.next_page === 'string' ? record.next_page : null;

  return {
    data: items,
    next_page: nextPage,
  };
}

export interface ApiErrorPayload {
  code?: string;
  message?: string;
}

/**
 * Extracts structured error information from API failure responses.
 * Supports: { success: false, error: { code, message } } and standard error envelopes.
 */
export function parseApiError(responseJSON: unknown): ApiErrorPayload | null {
  if (!responseJSON || typeof responseJSON !== 'object') {
    return null;
  }

  const record = responseJSON as Record<string, unknown>;

  if ('error' in record) {
    const err = record.error;
    if (typeof err === 'object' && err !== null) {
      const errRecord = err as Record<string, unknown>;
      return {
        code: typeof errRecord.code === 'string' ? errRecord.code : undefined,
        message: typeof errRecord.message === 'string' ? errRecord.message : undefined,
      };
    }
    if (typeof err === 'string') {
      return { message: err };
    }
  }

  if (typeof record.message === 'string') {
    return { message: record.message };
  }

  return null;
}
